import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import Dexie from 'dexie';
import { App } from './App';
import { DATABASE_NAME, LiftXDatabase, openDatabase } from './data/db';
import { mediaRepository } from './data/repositories/mediaRepository';
import { runScheduledBackupIfDue } from './data/backup/scheduledBackup';
import { parseBackupFile } from './data/backup/importBackup';
import { restore } from './data/backup/restoreEngine';
import { registerServiceWorker } from './pwa/registerSW';
import './ui/theme/tokens.css';
import './ui/theme/global.css';
import './ui/theme/buttons.css';
import './ui/theme/glass.css';

/**
 * Entry point.
 *
 * The database is opened before the first render so that a failure to open it
 * is a handled state rather than an exception thrown from inside a component
 * tree. Phase 0 §11.6 requires a recovery path, not a crash loop.
 */
async function start(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) throw new Error('#root is missing from index.html');

  try {
    await openDatabase();
  } catch (error) {
    renderFatal(container, error);
    return;
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  registerServiceWorker();

  // Fire-and-forget, deliberately not awaited: the orphan sweep (Phase 4B,
  // Phase 0 §8.4) is startup housekeeping, not a precondition for the app to
  // be usable. In normal operation it finds nothing — `mediaRepository`'s
  // add/remove/delete-exercise paths already keep `userMedia` and its owning
  // exercise consistent — this exists as a safety net against a bug or
  // external corruption, and must never delay first paint to run it.
  void mediaRepository.sweepOrphans();

  // Same convention: fire-and-forget startup housekeeping, never a
  // precondition for the app to be usable. Master Phase 9 — there is no
  // reliable background execution on an iOS Safari PWA, so "check on open,
  // write if due" is the only trustworthy moment for a scheduled backup.
  void runScheduledBackupIfDue();
}

/**
 * Last-resort screen when the database cannot be opened at all.
 *
 * Deliberately plain DOM: it must work even if React never mounts. Phase 0
 * §11.6 requires two real recovery actions here — "import a backup" and
 * "start fresh" — not just a dead-end message; Master Phase 9 adds them.
 * `window.confirm()` is used for both, a deliberate, scoped exception to the
 * app's usual custom-`alertdialog` convention: no React tree exists at this
 * failure point to render one.
 */
export function renderFatal(container: HTMLElement, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  container.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'alert');
  wrap.style.cssText =
    'padding:2rem;font-family:system-ui,sans-serif;max-width:34rem;margin:0 auto;color:#0b0c0e';

  const heading = document.createElement('h1');
  heading.textContent = 'LiftX could not start';
  heading.style.cssText = 'font-size:1.25rem;margin:0 0 .75rem';

  const body = document.createElement('p');
  body.textContent =
    'The local database could not be opened. Your workouts are stored on this device; ' +
    'do not clear website data. Try reopening the app, or use one of the options below.';
  body.style.cssText = 'margin:0 0 1rem;line-height:1.5';

  const detail = document.createElement('pre');
  detail.textContent = message;
  detail.style.cssText =
    'white-space:pre-wrap;font-size:.8125rem;background:#f4f6f8;padding:.75rem;border-radius:8px;margin:0 0 1.25rem';

  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.style.cssText = 'margin:0 0 1rem;line-height:1.5;color:#7a1f1a';

  const buttonStyle =
    'min-height:48px;padding:0 1rem;font:inherit;font-size:.9375rem;font-weight:600;' +
    'color:#0b0c0e;background:#fff;border:1px solid #ccc;border-radius:8px;cursor:pointer;margin-right:.5rem';

  const importButton = document.createElement('button');
  importButton.type = 'button';
  importButton.textContent = 'Import a backup';
  importButton.style.cssText = buttonStyle;

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.json,.zip';
  fileInput.style.display = 'none';

  importButton.onclick = () => fileInput.click();
  fileInput.onchange = () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (file === undefined) return;
    if (
      !window.confirm(
        'This replaces everything currently stored on this device with the imported backup. Continue?',
      )
    ) {
      return;
    }
    void (async () => {
      status.textContent = 'Restoring…';
      try {
        const parsed = await parseBackupFile(file);
        await Dexie.delete(DATABASE_NAME);
        const fresh = new LiftXDatabase();
        await openDatabase(fresh);
        await restore(parsed, 'replace', fresh);
        location.reload();
      } catch (cause) {
        status.textContent = cause instanceof Error ? `Could not restore: ${cause.message}` : 'Could not restore this backup.';
      }
    })();
  };

  const freshButton = document.createElement('button');
  freshButton.type = 'button';
  freshButton.textContent = 'Start fresh';
  freshButton.style.cssText = buttonStyle;
  freshButton.onclick = () => {
    if (!window.confirm('This permanently deletes everything currently stored on this device. Continue?')) return;
    void (async () => {
      status.textContent = 'Clearing…';
      try {
        await Dexie.delete(DATABASE_NAME);
        location.reload();
      } catch (cause) {
        status.textContent = cause instanceof Error ? `Could not clear the database: ${cause.message}` : 'Could not clear the database.';
      }
    })();
  };

  wrap.append(heading, body, detail, status, importButton, freshButton, fileInput);
  container.append(wrap);
}

void start();
